/**
 * components/ChartCard
 * The Overview's chart container: 9px radius, `16px 18px 18px` padding, and a
 * baseline-aligned header whose right slot carries the currency label the API
 * supplies ("AED only").
 *
 * That label is not decoration. Charts in this product are
 * base-currency-ONLY — they filter to the base currency rather than converting
 * into it — so the card has to say which currency the reader is looking at.
 */
import type { ReactNode } from 'react';
import { StyleSheet, Text, View } from 'react-native';
import { neutral } from '../../theme/colors';
import { radii } from '../../theme/radii';
import { mono, sans, size as fontSize } from '../../theme/typography';

export interface ChartCardProps {
  title: string;
  /** The mono label on the right — usually `charts.labelled` from the API. */
  note?: string;
  children: ReactNode;
}

export function ChartCard({ title, note, children }: ChartCardProps) {
  return (
    <View style={styles.card}>
      <View style={styles.header}>
        <Text style={styles.title}>{title}</Text>
        {!!note && (
          <Text numberOfLines={1} style={styles.note}>
            {note}
          </Text>
        )}
      </View>
      {children}
    </View>
  );
}

const styles = StyleSheet.create({
  card: {
    borderWidth: 1,
    borderColor: neutral.border,
    borderRadius: radii.cardBlock,
    backgroundColor: neutral.surface,
    paddingTop: 16,
    paddingHorizontal: 18,
    paddingBottom: 18,
  },
  header: {
    flexDirection: 'row',
    // Baseline, so a 13px title and a 10.5px mono note sit on the same line.
    alignItems: 'baseline',
    justifyContent: 'space-between',
    gap: 10,
    marginBottom: 14,
  },
  title: { ...sans('600'), fontSize: fontSize.body, color: neutral.ink, flexShrink: 1 },
  note: { ...mono('400'), fontSize: fontSize.tableHeader, color: neutral.grey400, flexShrink: 0 },
});

export default ChartCard;
